import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Col,
  ConfigProvider,
  Empty,
  Spin,
  Table,
  Tooltip,
  Typography,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { Paging } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BasePermissionCapability } from "app/based/baseApiModel/basePermissionMenu/BasePermissionCapability";
import { LocalContext } from "context/AuthenticationContext";
import {
  isArray,
  isEmpty,
  isNull,
  isNumber,
  isObject,
  isString,
  isUndefined,
} from "lodash";
import { ContextType, PureComponent, ReactNode } from "react";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { CurrencyEnum } from "utils/enums/CurrencyEnum";
import { ActionFormat } from "utils/format/ActionFormat";
import { colorFormat } from "utils/format/ColorFormat";
import { findFeature } from "utils/format/FeatureFormat";
import {
  checkClipboardReadPermission,
  formatCurrency,
  getMessage,
} from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import ActionButtonComponent from "../actionButtonComponent/ActionButtonComponent";
import ModalConfirmationComponent from "../modalConfirmation/ModalConfirmationComponent";
import {
  CustomSummaryProps,
  ITableComponentState,
  TableComponentModel,
} from "./model/TableComponentModel";

const { Text } = Typography;

export default class TableComponent<T extends Object> extends PureComponent<
  TableComponentModel<T>,
  ITableComponentState
> {
  static contextType = LocalContext;
  context!: ContextType<typeof LocalContext>;
  constructor(props: TableComponentModel<T>) {
    super(props);
    this.state = {
      detailPage: 0,
      detailPageSize: 10,
      savedCol: "",
    };
    this.handleDetailTableChange = this.handleDetailTableChange.bind(this);
    this.handleTotal = this.handleTotal.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
  }
  private isNeedToFormatKey = [
    "price",
    "balance",
    "quantity",
    "totalAmount",
    "credit",
    "cost",
  ];
  private isUnCopyable = [
    "status",
    "type",
    "simtype",
    "createdAt",
    "updatedAt",
    "ensign",
    "cover",
    "image",
  ];

  private get cachedCapability() {
    return productCache?.getCache(
      CacheKeyEnum.capabilities,
    ) as Array<BasePermissionCapability>;
  }

  private handleDetailTableChange = async (pagination?: Paging | any) => {
    this.setState({
      detailPage: pagination?.current! - 1,
      detailPageSize: pagination?.pageSize,
    });
  };

  private clickToCopy = async (
    value: unknown,
    key: string,
    colTitle: string | Object,
    needClickToCopy: boolean,
  ) => {
    let permisson = await checkClipboardReadPermission();
    // Permission was just granted from the dialog — the document is still
    // unfocused so any clipboard call would throw. Ask user to click again.
    if (permisson === "just-granted") {
      getMessage(strings.GlobalMessage.PermissonSuccess, 3, "success");
      return;
    }
    if (permisson === "granted" && needClickToCopy) {
      const { savedCol } = this.state;
      const isNeedToFormat = this.isNeedToFormatKey?.some((ele) =>
        key?.toLowerCase()?.includes(ele),
      );
      // readText() requires document focus. After the permission dialog is
      // dismissed, focus may not have returned yet — catch and skip duplicate check.
      let copiedValue: string | null = null;
      try {
        copiedValue = await navigator?.clipboard?.readText();
      } catch {
        // Document not focused (e.g. just after permission dialog); proceed without duplicate check.
      }
      const tagNames = document?.querySelectorAll("p, span, a");
      const resultTagName = tagNames?.forEach((ele) => {
        if (!!ele?.textContent && isString(ele?.textContent)) {
          return ele?.textContent;
        }
      });
      let finalColValue =
        isObject(colTitle) && !isEmpty(tagNames)
          ? isNeedToFormat
            ? new Intl.NumberFormat()?.format(resultTagName[0])
            : resultTagName[0]
          : (colTitle as string);

      const duplicatedCol = finalColValue === savedCol;
      let valueRendered = isNumber(value)
        ? new Intl.NumberFormat().format(value)
        : isString(value) && isNumber(parseInt(value))
          ? new Intl.NumberFormat().format(parseInt(value))
          : String(value);
      let finalValue = isNeedToFormat ? valueRendered : (value as string);

      if (finalValue === copiedValue && duplicatedCol) {
        getMessage(
          `${strings.GlobalMessage.AlreadyHasCopied} ${!isEmpty(finalColValue) ? `(${finalColValue})` : ``
          }: ${finalValue}`,
          3,
          "info",
        );
        return;
      } else {
        this.setState({
          savedCol: finalColValue,
        });
        await window?.navigator?.clipboard
          ?.writeText(finalValue)
          .then(() =>
            getMessage(
              `${strings.FileManagement.Placeholder.SavedValue} ${!isEmpty(finalColValue) ? `(${finalColValue})` : ``
              }: ${finalValue}`,
              4,
              "success",
            ),
          );
      }
    }
  };

  private handleRowClick = (record: T) => {
    let mousePressTime = 0;
    const { routeForSearching, keyForSearch } = this.props.data;
    const handleMouseDown = () => {
      mousePressTime = Date.now();
    };

    const handleMouseUp = () => {
      const clickDuration = Date.now() - mousePressTime;
      if (clickDuration < 200) {
        if (
          !!routeForSearching &&
          !!keyForSearch &&
          !isEmpty(keyForSearch) &&
          findFeature(keyForSearch)
        ) {
          window.open(
            `${routeForSearching}/${record["code"]}`,
            "_blank",
            "noreferrer",
          );
        }
      }
    };

    return {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
    };
  };

  private handleTotal = (total: number) => {
    const { summaryInfo, customSummary } = this.props?.data;

    const Item = (data: CustomSummaryProps) => {
      const { color, title, number } = data;
      return (
        <div className="flex items-center lg:text-base text-xs min-w-0">
          {title}:
          <Text
            style={{ color }}
            className="ml-2 lg:text-base text-xs truncate"
          >
            {number}
          </Text>
        </div>
      );
    };

    return (
      <div className="absolute left-0 flex flex-wrap w-1/2 xl:gap-x-8 gap-x-3 font-medium overflow-auto text-ellipsis whitespace-nowrap">
        <Item
          title={this.props?.data?.totalTitle}
          color={colorFormat?.Blue}
          number={total}
        />

        {!isEmpty(customSummary) ? (
          <>
            {customSummary?.map((item: CustomSummaryProps, index: number) => (
              <Item key={index} {...item} />
            ))}
          </>
        ) : (
          summaryInfo && (
            <>
              <div className="flex items-center lg:text-base text-xs min-w-0">
                {strings.GlobalTotalSummary.Success}:
                <Text
                  style={{ color: colorFormat?.Green }}
                  className="ml-2 lg:text-base text-xs truncate"
                >
                  {summaryInfo?.finished ?? 0}
                </Text>
              </div>

              <div className="flex items-center lg:text-base text-xs min-w-0">
                {strings.GlobalTotalSummary.Error}:
                <Text
                  style={{ color: "#E22A2A" }}
                  className="ml-2 lg:text-base text-xs truncate"
                >
                  {summaryInfo?.error ?? 0}
                </Text>
              </div>

              <div className="flex items-center lg:text-base text-xs min-w-0">
                {strings.GlobalTotalSummary.Other}:
                <Text
                  style={{ color: colorFormat?.Black }}
                  className="ml-2 lg:text-base text-xs truncate"
                >
                  {summaryInfo?.other ?? 0}
                </Text>
              </div>
              <div className="flex items-center lg:text-base text-xs min-w-0">
                <span className="mr-2">
                  {`${strings.GlobalTotalSummary.Revenue}`}:
                </span>
                <Tooltip
                  placement="bottomLeft"
                  arrow={false}
                  styles={{
                    body: { backgroundColor: "#FFFFFF" },
                    root: {
                      marginTop: "2px",
                    },
                  }}
                  title={
                    <div className="flex flex-col gap-2 py-1">
                      <div className="flex items-center lg:text-sm text-xs min-w-0">
                        <span className="mr-1 text-black">{`USD`}:</span>
                        {formatCurrency(
                          summaryInfo?.revenue?.USD ?? 0,
                          false,
                          true,
                          CurrencyEnum.$,
                        )}
                      </div>
                      <div className="flex items-center lg:text-sm text-xs min-w-0">
                        <span className="mr-1 text-black">{"VND"}:</span>
                        {formatCurrency(
                          summaryInfo?.revenue?.VND ?? 0,
                          false,
                          true,
                          CurrencyEnum.VND,
                        )}
                      </div>
                    </div>
                  }
                >
                  <InfoCircleOutlined style={{ color: colorFormat?.Green }} />
                </Tooltip>
              </div>
            </>
          )
        )}
      </div>
    );
  };

  render(): ReactNode {
    const {
      forDetail,
      page,
      pageSize,
      columns,
      dataSource,
      scroll,
      rowKey,
      loading,
      loadingTitle,
      total,
      customActionWidth,
      needRenderFeature = true,
      rowSelection,
      needClickToCopy = true,
      needActionButton = () => true,
      customActionChidren,
      onDeleteClick,
      onDetailClick,
      handleTableChange,
      deleteCapabilityKey = CapabilityEnum.delete,
      detailCapabilityKey = CapabilityEnum.detail,
    } = this.props.data;
    const { detailPage, detailPageSize } = this.state;
    const { isLargeScreen } = this.context;
    const tableScroll = scroll || { x: "max-content", y: 520 };
    const isHasDetailKey = this.cachedCapability?.some((item) =>
      item?.key?.includes(detailCapabilityKey),
    );
    const isHasDeleteKey = this.cachedCapability?.some((item) =>
      item?.key?.includes(deleteCapabilityKey),
    );

    const getValue = (obj, path) =>
      Array.isArray(path)
        ? path.reduce((acc, key) => acc?.[key], obj)
        : obj?.[path];

    const renderedColumns = [
      ...columns?.map((child) => ({
        ...child,
        ellipsis: true,
        align: child?.align || "center",
        onCell: (record) => {
          const originalOnCell = child["onCell"]?.(record) ?? {};
          return {
            ...originalOnCell,
            onClick: async () => {
              const dataIndex = child["dataIndex"];
              const recordVal = getValue(record, dataIndex);

              const indexKey = isArray(dataIndex)
                ? dataIndex[dataIndex.length - 1]?.toString()
                : dataIndex?.toString();
              if (
                !!indexKey &&
                !!recordVal &&
                typeof recordVal !== "object" &&
                !this.isUnCopyable?.some((text) =>
                  indexKey?.toLowerCase().includes(text.toLowerCase()),
                )
              ) {
                await this.clickToCopy(
                  recordVal,
                  indexKey,
                  child["title"],
                  needClickToCopy,
                );
              }
            },
          };
        },
      })),
      !isUndefined(onDetailClick) ||
        !isUndefined(onDeleteClick) ||
        !isUndefined(customActionChidren)
        ? {
          title: strings.GlobalLabels.Actions,
          fixed: isLargeScreen ? "right" : "auto",
          align: "center",
          width: !isUndefined(customActionWidth) ? customActionWidth : "9%",
          render: (record: T) => (
            <ActionButtonComponent
              data={{
                children:
                  !isUndefined(customActionChidren) &&
                  customActionChidren(record),
                buttonProps: !needActionButton?.(record)
                  ? []
                  : [
                    (!isUndefined(onDetailClick) && !needRenderFeature) ||
                      (isHasDetailKey &&
                        !isUndefined(onDetailClick) &&
                        needRenderFeature === true)
                      ? ActionFormat.renderActions({
                        keyword: detailCapabilityKey,
                        needFollowingFeature: needRenderFeature,
                        onClick: async () => await onDetailClick(record),
                        renderType: "detail",
                      })
                      : undefined,
                    (!isUndefined(onDeleteClick) && !needRenderFeature) ||
                      (isHasDeleteKey &&
                        !isUndefined(onDeleteClick) &&
                        needRenderFeature === true)
                      ? ActionFormat.renderActions({
                        keyword: deleteCapabilityKey,
                        renderType: "delete",
                        needFollowingFeature: needRenderFeature,
                        color: colorFormat?.Red_Dark,
                        onClick: async () =>
                          ModalConfirmationComponent({
                            data: {
                              title:
                                strings.GlobalMessage.DeleteConfirmation,
                              isDanger: true,
                              onOk: async () =>
                                await onDeleteClick(record),
                            },
                          }),
                      })
                      : undefined,
                  ]?.filter((child) => !isUndefined(child)),
              }}
            />
          ),
        }
        : undefined,
    ].filter(Boolean) as ColumnsType<T>;

    return (
      <Col span={24} className="mt-1">
        {forDetail === true ? (
          <Table
            columns={[
              {
                title: strings.GlobalLabels.STT,
                width: "4%",
                render: (item, record, index) => (
                  <span className="font-semibold">
                    {page * pageSize + index + 1}
                  </span>
                ),
                align: "center",
              },
              ...(needRenderFeature === true &&
                !isHasDetailKey &&
                !isHasDeleteKey &&
                isUndefined(customActionChidren)
                ? renderedColumns?.filter(
                  (item) => item?.title !== strings.GlobalLabels.Actions,
                )
                : renderedColumns?.filter((e) => !isUndefined(e))),
            ]}
            size="middle"
            showSorterTooltip={false}
            dataSource={dataSource}
            rowSelection={!!rowSelection ? rowSelection : null}
            onChange={this.handleDetailTableChange}
            scroll={{
              x: "max-content",
              y: (tableScroll && (tableScroll as any).y) || 520,
            }}
            rowKey={!!rowKey ? rowKey : null}
            className="h-full relative"
            pagination={{
              pageSize: detailPageSize,
              current: detailPage + 1,
              className: "w-1/2",
              simple: isLargeScreen ? false : true,
              total: dataSource?.length,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: this.handleTotal,
            }}
          />
        ) : (
          <Spin
            spinning={loading}
            indicator={
              <LoadingOutlined className="text-blue-600 font-semibold" />
            }
            tip={
              <span className="font-semibold text-blue-600">
                {loadingTitle}
              </span>
            }
          >
            <ConfigProvider
              renderEmpty={() => (
                <Empty
                  description={strings.GlobalLabels.NoInfo}
                  styles={{
                    image: {
                      fontSize: "12px",
                    },
                  }}
                />
              )}
              theme={{
                components: {
                  Table: {
                    rowSelectedBg: "fff",
                    rowSelectedHoverBg: "fff",
                  },
                },
              }}
            >
              <Table
                columns={[
                  {
                    title: strings.GlobalLabels.STT,
                    width: !isNull(tableScroll.x) ? "4%" : 80,
                    render: (item, record, index) => (
                      <span className="font-semibold">
                        {page * pageSize + index + 1}
                      </span>
                    ),
                    align: "center",
                  },
                  ...(needRenderFeature === true &&
                    !isHasDetailKey &&
                    !isHasDeleteKey &&
                    isUndefined(customActionChidren)
                    ? renderedColumns?.filter(
                      (item) => item?.title !== strings.GlobalLabels.Actions,
                    )
                    : renderedColumns?.filter((e) => !isUndefined(e))),
                ]}
                rowKey={rowKey}
                rowSelection={!!rowSelection ? rowSelection : null}
                size="middle"
                showSorterTooltip={false}
                dataSource={dataSource}
                onChange={handleTableChange}
                scroll={{
                  x: !isNull(tableScroll.x) ? tableScroll.x : "max-content",
                  y: (tableScroll && (tableScroll as any).y) || 520,
                }}
                className="h-full"
                pagination={{
                  pageSize: pageSize,
                  current: page + 1,
                  total: total,
                  simple: isLargeScreen ? false : true,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: this.handleTotal,
                }}
              />
            </ConfigProvider>
          </Spin>
        )}
      </Col>
    );
  }
}
